import moment from 'moment-timezone'
import { genericApiRequests } from '@/utilities/axios-factory'
import { findBusStops } from '@/services/bus-stops-service'
import { generateKeywordsQueryForOr, formatTime } from '@smarttransit/common'
import { addAlert, getCachedRegions } from '@/utilities/helpers'
import { getBusStopData } from '@/services/maps-service'
import { getBusStopsForProfileRoute } from '@/services/transportation-profile-routes-service'

export default {
  name: 'routes-events-bus-stop-pickup-times-dialog',
  props: {
    openModal: Boolean,
    isAdmin: Boolean,
    isNewEvent: Boolean,
    timeOnly: {
      type: Boolean,
      default: false
    },
    pickupTimes: {
      type: Array,
      default () {
        return []
      }
    },
    editingIndex: {
      type: Number,
      default: -1
    },
    busStopOnly: {
      type: Boolean,
      default: false
    },
    onModalClose: Function,
    onChange: Function
  },
  data () {
    return {
      internalOpen: false,
      busStopSearchKeywords: '',
      busStopSearchResults: [],
      busStopApiInProgress: false,
      selectedBusStop: null,
      suppressSearchWatcher: false,
      showBusStopMap: false,
      longLatOnMap: null,
      stMapApis: null,
      mapboxAccessToken: process.env.VUE_APP_MAPBOX_KEY,
      maptilerKey: process.env.VUE_APP_MAPTILER_KEY,
      pendingTimeValue: null,
      selectedTimes: [],
      editingGroup: null,
      form: {
        busStopId: null,
        label: ''
      }
    }
  },
  computed: {
    selectedTimesSorted () {
      const times = Array.isArray(this.selectedTimes) ? this.selectedTimes : []
      return times.filter(function (t) { return Boolean(t) }).slice().sort()
    },
    selectedTimesDisplay () {
      const sorted = this.selectedTimesSorted
      return sorted.map(function (raw) {
        return { raw, display: formatTime(raw) }
      })
    }
  },
  mounted () {
    this.internalOpen = this.$props.openModal

    if (this.internalOpen) {
      this.initFormFromProps()

      if (this.form.busStopId) {
        this.prefillSelectedBusStopFromResults()
      }
    }

    this.stMapApis = {
      genericApiRequests,
      addAlert,
      getBusStopsForProfileRoute,
      getBusStops: function ({ searchradius, ...rest }) {
        return getBusStopData(Object.assign({}, rest, { radius: searchradius }))
      },
      getCachedRegions
    }
  },
  watch: {
    selectedBusStop (newVal) {
      if (newVal?.id) {
        this.form.busStopId = newVal.id
        this.suppressSearchWatcher = true
        this.busStopSearchKeywords = newVal.name || ''

        if (this.busStopMapLoaded) {
          this.longLatOnMap = newVal.longlat
        }
      } else {
        this.form.busStopId = null
        this.suppressSearchWatcher = true
        this.busStopSearchKeywords = ''
      }
    },
    busStopSearchKeywords (newVal, oldVal) {
      if (this.suppressSearchWatcher) {
        this.suppressSearchWatcher = false
        return
      }

      const trimmed = (newVal || '').trim()
      const prevTrimmed = (oldVal || '').trim()

      if (!trimmed && prevTrimmed) {
        // this.busStopSearchResults = []
        this.busStopApiInProgress = false
        return
      }

      if (trimmed && trimmed !== prevTrimmed) {
        this.searchBusStops()
      }
    },
    openModal (newVal) {
      this.internalOpen = newVal

      if (newVal) {
        this.initFormFromProps()

        if (this.form.busStopId) {
          this.prefillSelectedBusStopFromResults()
        }
      }
    },
    editingIndex (newVal) {
      if (this.internalOpen && (newVal || newVal === 0)) {
        this.initFormFromProps()
      }
    }
  },
  methods: {
    getGroupForEntry (entry, index) {
      if (!entry) {
        return { type: 'index', index }
      }

      if (entry.busStopId) {
        return { type: 'stop', busStopId: entry.busStopId }
      }

      if (entry.label) {
        return { type: 'label', label: entry.label }
      }

      return { type: 'index', index }
    },
    isEntryInGroup (entry, index, group) {
      if (!group) {
        return false
      }

      if (group.type === 'stop') {
        return Boolean(entry && entry.busStopId && entry.busStopId === group.busStopId)
      }

      if (group.type === 'label') {
        return Boolean(entry && !entry.busStopId && entry.label && entry.label === group.label)
      }

      if (group.type === 'index') {
        return index === group.index
      }

      return false
    },
    normalizeSelectedTimes () {
      const times = Array.isArray(this.selectedTimes) ? this.selectedTimes : []
      const seen = {}
      const deduped = []

      for (let i = 0; i < times.length; i++) {
        const t = times[i]
        if (!t || seen[t]) {
          continue
        }
        seen[t] = true
        deduped.push(t)
      }

      deduped.sort()
      this.selectedTimes = deduped
    },
    addPendingTime () {
      if (!this.pendingTimeValue) {
        alert('Please provide a pickup time')
        return
      }

      const timeStr = moment.utc(this.pendingTimeValue).format('HH:mm')
      if (!timeStr) {
        alert('Please provide a pickup time')
        return
      }

      const times = Array.isArray(this.selectedTimes) ? this.selectedTimes.slice() : []
      times.push(timeStr)
      this.selectedTimes = times
      this.normalizeSelectedTimes()
      this.pendingTimeValue = null
    },
    removeSelectedTime (raw) {
      const times = Array.isArray(this.selectedTimes) ? this.selectedTimes : []
      this.selectedTimes = times.filter(function (t) { return t !== raw })
    },
    async onBusStopClicked (feature, e) {
      if (!feature || !feature.properties || !feature.properties.id) {
        return
      }

      const externalId = feature.properties.id
      let busStop = null

      // Try to find in current autocomplete results first
      if (Array.isArray(this.busStopSearchResults) && this.busStopSearchResults.length) {
        busStop = this.busStopSearchResults.find(function (o) {
          return o && o.externalId === externalId
        }) || null
      }

      // Fallback: query API by externalId to get the canonical bus stop (with DB id)
      if (!busStop) {
        try {
          const results = await findBusStops({
            where: { externalId },
            limit: 1
          })

          if (results && results.length) {
            busStop = results[0]
          }
        } catch (err) {
          addAlert({
            message: err,
            type: 'error'
          })
        }
      }

      if (!busStop) {
        addAlert({
          message: 'No bus stop found for the selected map marker',
          type: 'error'
        })
        return
      }

      // Ensure the selected stop exists in the autocomplete items
      if (!Array.isArray(this.busStopSearchResults)) {
        this.busStopSearchResults = []
      }

      const existsInResults = this.busStopSearchResults.some(function (o) {
        return o && o.id === busStop.id
      })

      if (!existsInResults) {
        this.busStopSearchResults = this.busStopSearchResults.concat([busStop])
      }

      // Setting selectedBusStop will flow through the watcher and update
      // form.busStopId and the visible search text
      this.selectedBusStop = busStop
    },
    onDialogToggle (newVal) {
      if (!newVal) {
        this.closeDialog()
      }
    },
    closeDialog () {
      this.internalOpen = false

      if (this.$props.onModalClose) {
        this.$props.onModalClose()
      }
    },
    initFormFromProps () {
      const index = typeof this.editingIndex === 'number' ? this.editingIndex : -1
      const list = Array.isArray(this.pickupTimes) ? this.pickupTimes : []

      if (index >= 0 && index < list.length) {
        const item = list[index] || {}
        this.form.busStopId = item.busStopId || null
        this.form.label = item.label || ''

        // Edit mode behavior:
        // - busStopOnly: no times
        // - timeOnly: start with just this time (user may add more)
        // - outbound (time + stop): edit all times for this stop/label group
        this.editingGroup = (!this.$props.busStopOnly && !this.$props.timeOnly)
          ? this.getGroupForEntry(item, index)
          : null

        if (this.$props.busStopOnly) {
          this.selectedTimes = []
        } else if (this.$props.timeOnly) {
          this.selectedTimes = item.time ? [item.time] : []
        } else {
          const group = this.editingGroup
          const groupTimes = []

          for (let i = 0; i < list.length; i++) {
            const entry = list[i]
            if (this.isEntryInGroup(entry, i, group) && entry && entry.time) {
              groupTimes.push(entry.time)
            }
          }

          this.selectedTimes = groupTimes
        }
      } else {
        this.form.busStopId = null
        this.form.label = ''
        this.selectedTimes = []
        this.editingGroup = null
      }

      this.normalizeSelectedTimes()
      this.pendingTimeValue = null

      this.selectedBusStop = null
      this.busStopSearchKeywords = ''

      if (this.form.busStopId) {
        this.prefillSelectedBusStopFromResults()

        if (!this.selectedBusStop) {
          findBusStops({
            where: { id: this.form.busStopId },
            limit: 1
          }).then((results) => {
            this.busStopSearchResults = results?.length ? results : []
            this.prefillSelectedBusStopFromResults()
          })
        }
      }

      this.showBusStopMap = false
      this.longLatOnMap = null
    },
    prefillSelectedBusStopFromResults () {
      const id = this.form.busStopId
      if (!id || !Array.isArray(this.busStopSearchResults)) {
        return
      }

      const existing = this.busStopSearchResults.find(function (x) { return x && x.id === id })

      if (existing) {
        this.selectedBusStop = existing
      }
    },
    viewBusStopInMap () {
      if (this.busStopMapLoaded) {
        this.longLatOnMap = this.selectedBusStop.longlat
      }

      this.showBusStopMap = true
    },
    hideBusStopMap () {
      this.busStopMapLoaded = false
      this.showBusStopMap = false
    },
    onBusStopMapLoaded () {
      this.busStopMapLoaded = true

      if (this.selectedBusStop?.longlat) {
        this.longLatOnMap = this.selectedBusStop.longlat
      }
    },
    async searchBusStops () {
      const keywords = (this.busStopSearchKeywords || '').trim()

      if (!keywords) {
        this.busStopSearchResults = []
        return
      }

      this.busStopApiInProgress = true
      try {
        const filter = {
          where: {},
          limit: 10,
          order: 'name ASC'
        }

        filter.where.or = generateKeywordsQueryForOr(keywords, ['name', 'externalName', 'externalId'])

        const results = await findBusStops(filter)
        this.busStopSearchResults = Array.isArray(results) ? results : []
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.busStopApiInProgress = false
      }
    },
    selectBusStopIfNeeded () {
      const busStop = this.selectedBusStop
      if (!busStop || !busStop.id) {
        this.form.busStopId = null
        return
      }

      this.form.busStopId = busStop.id
    },
    async onSave () {
      if (!this.isAdmin) {
        return
      }

      // If a time is currently selected but not added to the list yet, add it.
      if (!this.$props.busStopOnly && this.pendingTimeValue) {
        this.addPendingTime()
      }

      if (!this.$props.busStopOnly && (!Array.isArray(this.selectedTimes) || !this.selectedTimes.length)) {
        alert('Please add at least one pickup time')
        return
      }

      if (!this.$props.timeOnly) {
        // Ensure form.busStopId matches the currently selected bus stop
        this.selectBusStopIfNeeded()

        if (!this.form.busStopId && !this.form.label) {
          alert('Please select a bus stop or provide a label')
          return
        }
      }

      const list = Array.isArray(this.pickupTimes) ? this.pickupTimes.slice() : []
      const index = typeof this.editingIndex === 'number' ? this.editingIndex : -1

      // busStopOnly stays single-entry (no times)
      if (this.$props.busStopOnly) {
        const newEntry = {
          busStopId: this.form.busStopId,
          label: this.form.label || null,
          time: null
        }

        if (index >= 0 && index < list.length) {
          list.splice(index, 1, newEntry)
        } else {
          list.push(newEntry)
        }

        if (this.$props.onChange) {
          this.$props.onChange(list)
        }

        this.pendingTimeValue = null
        this.selectedTimes = []
        this.editingGroup = null
        this.closeDialog()
        return
      }

      const busStopIdToSave = this.$props.timeOnly ? null : this.form.busStopId
      const labelToSave = this.$props.timeOnly ? null : (this.form.label || null)
      const timesToSave = this.selectedTimesSorted

      const newEntries = timesToSave.map(function (t) {
        return {
          busStopId: busStopIdToSave,
          label: labelToSave,
          time: t
        }
      })

      if (this.$props.timeOnly) {
        // Replace the single edited item with N items (or append in add mode)
        if (index >= 0 && index < list.length) {
          list.splice.apply(list, [index, 1].concat(newEntries))
        } else {
          list.push.apply(list, newEntries)
        }
      } else {
        // Outbound: edit replaces the entire stop/label group, not just one item.
        if (index >= 0 && index < list.length && this.editingGroup) {
          let insertAt = null
          const kept = []

          for (let i = 0; i < list.length; i++) {
            const entry = list[i]
            const shouldRemove = this.isEntryInGroup(entry, i, this.editingGroup)
            if (shouldRemove) {
              if (insertAt === null) {
                insertAt = kept.length
              }
              continue
            }

            kept.push(entry)
          }

          if (insertAt === null) {
            insertAt = kept.length
          }

          kept.splice.apply(kept, [insertAt, 0].concat(newEntries))
          list.splice(0, list.length)
          list.push.apply(list, kept)
        } else {
          list.push.apply(list, newEntries)
        }
      }

      if (this.$props.onChange) {
        this.$props.onChange(list)
      }

      this.pendingTimeValue = null
      this.selectedTimes = []
      this.editingGroup = null
      this.closeDialog()
    }
  }
}
